import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { DEFAULT_GOAL_NAME, getGoalNameErrors, getValidationErrors } from './GoalDrawer.utils';

export interface IUseGoalDrawerFormReturn {
  acknowledged: boolean;
  activeCampaignsCount: number;
  /** Errors shown on the submit button. */
  buttonErrors: string[];
  goal: Goal | null;
  handleSave: () => Promise<void>;
  isSaving: boolean;
  isValid: boolean;
  /** Errors shown inline below the name field. */
  nameErrors: string[];
  reset: () => void;
  setAcknowledged: (value: boolean) => void;
  totalConversions: number;
}

/**
 * Manages ephemeral goal creation, validation, and saving for the GoalDrawer.
 *
 * @param onClose - Callback to close the drawer.
 * @param isOpen - Whether the drawer is currently open.
 * @returns Form state and handlers for the GoalDrawer.
 */
export function useGoalDrawerForm(
  onClose: () => void,
  isOpen: boolean,
  mode: 'create' | 'edit' = 'create',
  editGoal?: Goal,
): IUseGoalDrawerFormReturn {
  const { t } = useTranslation();
  const { Goals } = useStore();

  const [acknowledged, setAcknowledged] = useState(false);
  const [createdGoal, setCreatedGoal] = useState<Goal | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (mode === 'create' && isOpen) {
      setCreatedGoal(
        Goals.create({
          kind: 'account',
          name: DEFAULT_GOAL_NAME,
        }),
      );
    }
  }, [isOpen, mode]);

  const goal = mode === 'edit' ? editGoal ?? null : createdGoal;

  const currentGoalName = goal?.get('name') ?? '';

  const allNameErrors = useMemo(() => {
    if (!goal) {
      return [];
    }
    const existingNames = Goals.list()
      .models.filter((g) => !g.isEphemeral && (mode === 'create' || g.id !== goal.id))
      .map((g) => g.get('name'));

    return getGoalNameErrors(currentGoalName, existingNames, t);
  }, [goal, currentGoalName, mode]);

  const isPlaceholderName = mode === 'create' && currentGoalName === DEFAULT_GOAL_NAME;
  const nameErrors = isPlaceholderName ? [] : allNameErrors;

  const isGoalModelValid = goal?.isValid() ?? false;
  const isValid =
    goal !== null &&
    allNameErrors.length === 0 &&
    isGoalModelValid &&
    (mode === 'create' || acknowledged);

  const buttonErrors = getValidationErrors(allNameErrors, goal);
  if (mode === 'edit' && !acknowledged) {
    buttonErrors.push(t('Acknowledge the warning above to save.'));
  }

  // TODO: switch once backend stats are implemented; edit shows stat - create shows 0
  const activeCampaignsCount: number = 0;
  const totalConversions: number = 0;

  const reset = useCallback((): void => {
    if (mode === 'create' && createdGoal) {
      Goals.removeEphemeral(createdGoal.id);
      setCreatedGoal(null);
    } else if (mode === 'edit' && editGoal) {
      editGoal.discardDirty();
    }
    setAcknowledged(false);
  }, [mode, createdGoal, Goals, editGoal]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!goal) {
      return;
    }

    setIsSaving(true);

    try {
      if (mode === 'edit') {
        await goal.save();
        toast(
          t("Goal '{{- name}}' updated — changes applied across all campaigns and flights.", {
            name: goal.get('name'),
          }),
          { type: 'success' },
        );
      } else {
        await Goals.add(goal);
        toast(
          t("Goal '{{- name}}' created — now available to select in campaigns and flights.", {
            name: goal.get('name'),
          }),
          { type: 'success' },
        );
        setCreatedGoal(null);
      }
      onClose();
    } catch (error) {
      // TODO: surface server-side name uniqueness errors inline once anhinga#7380 adds validation
      showError(error, t);
    } finally {
      setIsSaving(false);
    }
  }, [goal, mode, onClose, t, Goals]);

  return {
    acknowledged,
    activeCampaignsCount,
    buttonErrors,
    goal,
    handleSave,
    isSaving,
    isValid,
    nameErrors,
    reset,
    setAcknowledged,
    totalConversions,
  };
}
